<script setup>
import { ref, computed, nextTick, watch, onUnmounted } from 'vue';
import { useFlowieHandoff } from '@/composables/useFlowieHandoff'

const props = defineProps({
    messages: { type: Array, default: () => [] },
    isGenerating: { type: Boolean, default: false },
    initialMessage: { type: String, default: '' },
});

const emit = defineEmits(['send']);

const messageInput = ref('');

const { flowieComposerVisible, closeFlowieComposer } = useFlowieHandoff(
    () => props.initialMessage,
    messageInput
);
const messagesContainer = ref(null);
const funMessageIndex = ref(0);
const funMessageInterval = ref(null);
const progressWidth = ref(0);
const progressInterval = ref(null);

const suggestedQuestions = [
    { text: 'How many leads have I added?', icon: 'M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z' },
    { text: 'What is my total revenue?', icon: 'M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z' },
    { text: 'How are my newsletters performing?', icon: 'M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75' },
    { text: 'Show me my deal pipeline', icon: 'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z' },
    { text: 'What are my growth trends this month?', icon: 'M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941' },
    { text: 'How many form submissions do I get?', icon: 'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z' },
    { text: 'Give me a business overview', icon: 'M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21' },
    { text: 'Compare revenue vs new contacts over 6 months', icon: 'M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0020.25 18V6A2.25 2.25 0 0018 3.75H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 20.25z' },
];

const funMessages = [
    { icon: 'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z', text: 'Crunching your numbers...' },
    { icon: 'M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941', text: 'Analyzing trends...' },
    { icon: 'M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375', text: 'Querying your data...' },
    { icon: 'M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0020.25 18V6A2.25 2.25 0 0018 3.75H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 20.25z', text: 'Correlating metrics...' },
    { icon: 'M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5', text: 'Building visualizations...' },
    { icon: 'M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z', text: 'Finding patterns...' },
    { icon: 'M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z', text: 'Almost there...' },
    { icon: 'M10.5 6a7.5 7.5 0 107.5 7.5h-7.5V6z M13.5 10.5H21A7.5 7.5 0 0013.5 3v7.5z', text: 'Preparing your report...' },
];

const currentFunMessage = computed(() => funMessages[funMessageIndex.value % funMessages.length]);

const startFunMessages = () => {
    funMessageIndex.value = Math.floor(Math.random() * funMessages.length);
    progressWidth.value = 0;

    funMessageInterval.value = setInterval(() => {
        funMessageIndex.value = (funMessageIndex.value + 1) % funMessages.length;
    }, 3500);

    let elapsed = 0;
    progressInterval.value = setInterval(() => {
        elapsed += 200;
        progressWidth.value = Math.min(90, 30 * Math.log10(elapsed / 500 + 1));
    }, 200);
};

const stopFunMessages = () => {
    if (funMessageInterval.value) {
        clearInterval(funMessageInterval.value);
        funMessageInterval.value = null;
    }
    if (progressInterval.value) {
        clearInterval(progressInterval.value);
        progressInterval.value = null;
    }
    progressWidth.value = 100;
};

watch(() => props.isGenerating, (val) => {
    if (val) startFunMessages();
    else stopFunMessages();
});

const sendMessage = () => {
    const text = messageInput.value.trim();
    if (!text || props.isGenerating) return;
    messageInput.value = '';
    emit('send', text);
};

const handleKeydown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
    }
};

watch(() => props.messages.length, () => {
    nextTick(() => {
        if (messagesContainer.value) {
            messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
        }
    });
});

onUnmounted(() => {
    stopFunMessages();
});

const renderMarkdown = (text) => {
    if (!text) return '';
    let html = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    // Headings
    html = html.replace(/^#### (.+)$/gm, '<h4 class="font-semibold text-sm text-gray-700 mt-3 mb-1">$1</h4>');
    html = html.replace(/^### (.+)$/gm, '<h3 class="font-semibold text-sm text-gray-800 mt-3 mb-1">$1</h3>');
    html = html.replace(/^## (.+)$/gm, '<h2 class="font-bold text-base text-gray-900 mt-2 mb-1">$1</h2>');

    // Bold & italic
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');

    // Tables
    html = html.replace(/^\|(.+)\|\s*\n\|[-| :]+\|\s*\n((?:\|.+\|\s*\n?)*)/gm, (match, headerRow, bodyRows) => {
        const headers = headerRow.split('|').map(h => h.trim()).filter(Boolean);
        const headerHtml = headers.map(h => `<th class="px-2 py-1 text-left text-xs font-semibold text-gray-600 border-b border-gray-300">${h}</th>`).join('');
        const rows = bodyRows.trim().split('\n').map(row => {
            const cells = row.replace(/^\||\|$/g, '').split('|').map(c => c.trim());
            return '<tr>' + cells.map(c => `<td class="px-2 py-1 text-xs text-gray-700 border-b border-gray-100">${c}</td>`).join('') + '</tr>';
        }).join('');
        return `<table class="w-full my-2 border-collapse"><thead><tr>${headerHtml}</tr></thead><tbody>${rows}</tbody></table>`;
    });

    // Lists
    html = html.replace(/^(\d+)\.\s+(.+)$/gm, '<li class="ml-4 list-decimal">$2</li>');
    html = html.replace(/^[-•]\s+(.+)$/gm, '<li class="ml-4 list-disc">$1</li>');

    // Wrap consecutive <li> in <ul>/<ol>
    html = html.replace(/((?:<li class="ml-4 list-disc">.+<\/li>\s*)+)/g, '<ul class="my-1 space-y-0.5">$1</ul>');
    html = html.replace(/((?:<li class="ml-4 list-decimal">.+<\/li>\s*)+)/g, '<ol class="my-1 space-y-0.5">$1</ol>');

    // Paragraphs
    html = html.replace(/\n\n/g, '</p><p class="mt-2">');
    html = html.replace(/\n/g, '<br>');

    // Clean up: remove <p> tags wrapping block elements
    html = '<p>' + html + '</p>';
    html = html.replace(/<p>\s*(<h[2-4])/g, '$1');
    html = html.replace(/(<\/h[2-4]>)\s*<\/p>/g, '$1');
    html = html.replace(/<p>\s*(<table)/g, '$1');
    html = html.replace(/(<\/table>)\s*<\/p>/g, '$1');
    html = html.replace(/<p>\s*(<ul|<ol)/g, '$1');
    html = html.replace(/(<\/ul>|<\/ol>)\s*<\/p>/g, '$1');
    html = html.replace(/<p>\s*<\/p>/g, '');

    return html;
};
</script>

<template>
    <div class="flex-1 flex flex-col overflow-hidden">
        <flowie-handoff-composer
            v-model="messageInput"
            :visible="flowieComposerVisible"
            :disabled="isGenerating"
            :placeholder="_mft('data-analyst:susan.placeholder')"
            @close="closeFlowieComposer"
            @send="sendMessage"
        />

        <!-- Messages -->
        <div ref="messagesContainer" class="flex-1 overflow-y-auto p-4 space-y-3">
            <!-- Empty state -->
            <div v-if="messages.length === 0 && !isGenerating" class="flex flex-col items-center text-gray-400 text-sm mt-8 px-4">
                <div class="flex items-center justify-center w-12 h-12 mb-3 rounded-full bg-aiAgentDataAnalyst">
                    <ai-agent-data-analyst-avatar class="w-full h-full" />
                </div>
                <p class="mb-1">
                    <mf-translate domainkey="data-analyst:susan.emptyState"></mf-translate>
                </p>
                <p class="text-xs mb-4">
                    <mf-translate domainkey="data-analyst:susan.emptyStateExample"></mf-translate>
                </p>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-xl">
                    <button
                        v-for="(q, qi) in suggestedQuestions"
                        :key="qi"
                        @click="emit('send', q.text)"
                        class="flex items-center gap-2.5 px-3 py-2.5 text-left text-sm text-gray-600 bg-white border border-gray-200 rounded-xl hover:border-aiAgentDataAnalyst hover:bg-aiAgentDataAnalyst/40 hover:text-gray-800 transition-colors duration-150"
                    >
                        <svg class="w-4 h-4 flex-shrink-0 text-gray-700" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" :d="q.icon" />
                        </svg>
                        <span>{{ q.text }}</span>
                    </button>
                </div>
            </div>

            <!-- Message list -->
            <div
                v-for="(msg, index) in messages"
                :key="index"
                :class="msg.role === 'user' ? 'flex justify-end' : 'flex justify-start'"
            >
                <!-- User messages -->
                <div
                    v-if="msg.role === 'user'"
                    class="bg-aiAgentDataAnalyst text-gray-900 rounded-2xl rounded-br-md px-4 py-2 max-w-[85%] text-sm whitespace-pre-line"
                >
                    {{ msg.content }}
                </div>

                <!-- Assistant messages -->
                <div v-else class="max-w-[90%] space-y-2">
                    <div class="bg-gray-100 text-gray-800 rounded-2xl rounded-bl-md px-4 py-2 text-sm" v-html="renderMarkdown(msg.content)">
                    </div>

                    <utility-ai-usage-cta v-if="msg.budgetExceeded" class="ml-1" />

                    <!-- Charts -->
                    <div v-if="msg.charts && msg.charts.length" class="space-y-3 ml-1">
                        <div v-for="(chart, ci) in msg.charts" :key="ci" class="p-3 bg-white rounded-lg border border-gray-200">
                            <p class="text-xs font-semibold text-gray-600 mb-2">{{ chart.title }}</p>
                            <component
                                :is="'chart-' + chart.type"
                                :labels="chart.labels"
                                :series="chart.series"
                                :fill-colors="['#10b981', '#6366f1', '#f59e0b', '#ef4444', '#8b5cf6']"
                                :show-tooltip="true"
                                :show-legend="Array.isArray(chart.series) && chart.series.length > 1 && typeof chart.series[0] === 'object'"
                                :data-label-prefix="chart.prefix || ''"
                                :data-label-suffix="chart.suffix || ''"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <!-- Generating animation -->
            <div v-if="isGenerating" class="flex justify-start generating-appear">
                <div class="w-full max-w-[90%] rounded-2xl rounded-bl-md overflow-hidden shadow-lg border border-aiAgentDataAnalyst bg-gradient-to-br from-aiAgentDataAnalyst/80 via-white to-aiAgentDataAnalyst/30">
                    <div class="h-1 w-full bg-gray-200 overflow-hidden">
                        <div
                            class="h-full bg-aiAgentDataAnalyst transition-all duration-700 ease-out"
                            :style="{ width: progressWidth + '%' }"
                        ></div>
                    </div>
                    <div class="px-5 py-4">
                        <div class="flex items-center gap-3 mb-3">
                            <div class="generating-icon flex-shrink-0 w-8 h-8 rounded-lg bg-aiAgentDataAnalyst flex items-center justify-center" :key="funMessageIndex">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-800" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                    <path stroke-linecap="round" stroke-linejoin="round" :d="currentFunMessage.icon" />
                                </svg>
                            </div>
                            <span class="text-sm font-medium text-gray-700 generating-text" :key="'t' + funMessageIndex">{{ currentFunMessage.text }}</span>
                        </div>
                        <div class="space-y-2">
                            <div class="flex gap-2">
                                <div class="h-2 rounded-full bg-aiAgentDataAnalyst/60 skeleton-shimmer" style="width: 65%"></div>
                                <div class="h-2 rounded-full bg-aiAgentDataAnalyst/40 skeleton-shimmer" style="width: 25%; animation-delay: 0.3s"></div>
                            </div>
                            <div class="flex gap-2">
                                <div class="h-2 rounded-full bg-aiAgentDataAnalyst/40 skeleton-shimmer" style="width: 40%; animation-delay: 0.15s"></div>
                                <div class="h-2 rounded-full bg-aiAgentDataAnalyst/60 skeleton-shimmer" style="width: 50%; animation-delay: 0.45s"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Input area -->
        <div class="border-t border-gray-200 p-3">
            <div class="flex items-end space-x-2">
                <textarea
                    v-model="messageInput"
                    @keydown="handleKeydown"
                    :disabled="isGenerating"
                    :placeholder="_mft('data-analyst:susan.placeholder')"
                    rows="2"
                    class="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 disabled:opacity-50"
                ></textarea>
                <button-base
                    @click="sendMessage"
                    style-type="primary"
                    :disabled="!messageInput.trim() || isGenerating"
                    size="sm"
                    class="flex-shrink-0 !px-2 !py-2"
                >
                    <template #icon>
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"/>
                        </svg>
                    </template>
                </button-base>
            </div>
        </div>
    </div>
</template>

<style scoped>
@keyframes skeleton-shimmer {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 1; }
}
.skeleton-shimmer {
    animation: skeleton-shimmer 2s ease-in-out infinite;
}

@keyframes generating-appear {
    from { opacity: 0; transform: translateY(8px) scale(0.97); }
    to { opacity: 1; transform: translateY(0) scale(1); }
}
.generating-appear {
    animation: generating-appear 0.4s ease-out;
}

@keyframes icon-pop {
    0% { transform: scale(0) rotate(-20deg); }
    60% { transform: scale(1.15) rotate(5deg); }
    100% { transform: scale(1) rotate(0deg); }
}
.generating-icon {
    animation: icon-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes text-slide {
    from { opacity: 0; transform: translateX(-8px); }
    to { opacity: 1; transform: translateX(0); }
}
.generating-text {
    animation: text-slide 0.4s ease-out;
}
</style>
